import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSetup } from "../lib/auth";
import { ApiError } from "../lib/api";

export function SetupPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const usernameHint = params.get("u"); // optional pre-fill for Safari-Keychain (Setup-CLI kann das mitgeben)

  const setup = useSetup();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [doneFor, setDoneFor] = useState<string | null>(null);

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center px-5 bg-ios-bg">
        <div className="ios-card max-w-sm w-full p-6 text-center animate-fade-in">
          <h1 className="text-[22px] font-semibold tracking-tight mb-2">Ungültiger Setup-Link</h1>
          <p className="text-[15px] text-ios-muted">
            Dieser Link enthält keinen Setup-Token. Bitte verwende die Setup-URL aus dem CLI-Output.
          </p>
        </div>
      </div>
    );
  }

  if (doneFor) {
    const loginHref = `/login?username=${encodeURIComponent(doneFor)}`;
    return (
      <div className="min-h-screen grid place-items-center px-5 bg-ios-bg">
        <div className="ios-card max-w-sm w-full p-6 text-center animate-slide-up">
          <div className="w-14 h-14 mx-auto rounded-full bg-ios-green/15 grid place-items-center text-3xl mb-3">
            ✓
          </div>
          <h1 className="text-[22px] font-semibold tracking-tight mb-1">Passwort gesetzt</h1>
          <p className="text-[15px] text-ios-muted mb-5">
            Konto „{doneFor}" ist aktiv. Du kannst dich jetzt anmelden.
          </p>
          <Link to={loginHref} className="ios-btn-primary w-full">
            Zur Anmeldung
          </Link>
        </div>
      </div>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 12) {
      setError("Passwort muss mindestens 12 Zeichen lang sein.");
      return;
    }
    if (password !== confirm) {
      setError("Passwörter stimmen nicht überein.");
      return;
    }
    try {
      const res = await setup.mutateAsync({ token, password });
      setDoneFor(res.username);
    } catch (e) {
      if (e instanceof ApiError && e.status === 410) {
        setError("Setup-Link ist abgelaufen oder bereits verwendet.");
      } else if (e instanceof ApiError) {
        setError(e.message);
      } else {
        setError("Verbindungsfehler.");
      }
    }
  }

  return (
    <div className="min-h-screen grid place-items-center px-5 py-12 bg-ios-bg">
      <div className="w-full max-w-sm animate-slide-up">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto rounded-iosXl bg-ios-blue grid place-items-center text-white text-3xl shadow-card mb-4">
            🧾
          </div>
          <h1 className="text-[28px] font-semibold tracking-tight">Konto einrichten</h1>
          <p className="text-[15px] text-ios-muted mt-1">
            Wähle ein sicheres Passwort (mindestens 12 Zeichen).
          </p>
        </div>

        <form onSubmit={onSubmit} className="ios-card p-5 space-y-3" autoComplete="on">
          {/* Hidden username für iCloud-Keychain-Speicherung — nur wenn ?u=… vorhanden */}
          {usernameHint && (
            <input
              type="text"
              name="username"
              autoComplete="username"
              value={usernameHint}
              readOnly
              className="hidden"
            />
          )}
          <input
            type="password"
            name="new-password"
            autoComplete="new-password"
            placeholder="Neues Passwort"
            className="ios-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={12}
            autoFocus
          />
          <input
            type="password"
            name="new-password-confirm"
            autoComplete="new-password"
            placeholder="Passwort wiederholen"
            className="ios-input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
          {error && (
            <div className="text-[13px] text-ios-red bg-ios-red/10 rounded-ios px-3 py-2">
              {error}
            </div>
          )}
          <button
            type="submit"
            className="ios-btn-primary w-full"
            disabled={setup.isPending || password.length < 12 || password !== confirm}
          >
            {setup.isPending ? "Speichern…" : "Passwort setzen"}
          </button>
          <p className="text-[12px] text-ios-muted text-center mt-2">
            Safari schlägt ein sicheres Passwort vor und speichert es im iCloud-Schlüsselbund.
          </p>
        </form>
      </div>
    </div>
  );
}
